import { errorLog, readableTimestamp } from "@zyzgroup/core-common";
import { onDocumentReady } from "./hooks/useElement";

export class WebMonitor {
  appId: string;
  reportUrl?: string;

  constructor(options: {
    appId: string;
    reportUrl?: string;
    checkWhiteScreen?: boolean;
  }) {
    this.appId = options.appId;
    this.reportUrl = options.reportUrl;
    this.listenJSError();
    this.listenAssetsError();
    this.listenPromiseError();
    if (options.checkWhiteScreen) {
      this.listenWhiteScreenError();
    }
  }

  // 捕获绝大多数运行时错误
  listenJSError() {
    window.onerror = (message, source, lineno, colno, error) => {
      const log = {
        type: "js_error",
        msg: message,
        file: source,
        row: lineno,
        col: colno,
        stack: error?.stack
      };
      this.report(log);
    };
  }

  // 资源加载错误
  listenAssetsError() {
    window.addEventListener(
      "error",
      (event) => {
        const target = event.target;
        // 排除 JS 报错
        if (
          target instanceof HTMLScriptElement ||
          target instanceof HTMLStyleElement ||
          target instanceof HTMLImageElement
        ) {
          const log = {
            type: "resource_error",
            filename: (target as any).src || (target as any).href,
            tagName: target.tagName
          };
          this.report(log);
        }
      },
      true // 资源错误不会冒泡
    );
  }

  // Promise 报错
  listenPromiseError() {
    window.addEventListener(
      "unhandledrejection",
      (event: PromiseRejectionEvent) => {
        const log = {
          type: "promise_error",
          reason: event.reason
        };
        this.report(log);
      }
    );
  }

  // 白屏检测
  listenWhiteScreenError() {
    // 页面加载 3 秒后检测
    onDocumentReady(() => {
      setTimeout(() => {
        const points = [
          [window.innerWidth / 2, window.innerHeight / 2], // 中心点
          [window.innerWidth / 2, window.innerHeight * 0.2], // 上
          [window.innerWidth / 2, window.innerHeight * 0.8], // 下
          [window.innerWidth * 0.2, window.innerHeight / 2], // 左
          [window.innerWidth * 0.8, window.innerHeight / 2] // 右
          // ... 可以多取几个点
        ];

        let wrapperElements = 0;

        points.forEach((point) => {
          const [x, y] = point;
          // 获取坐标处的元素列表
          const elements = document.elementsFromPoint(x, y);
          const topElement = elements[0]; // 最上层元素

          // 如果最上层是 body 或 html 或 骨架屏的那个div，说明没内容覆盖
          if (
            topElement?.tagName === "BODY" ||
            topElement?.tagName === "HTML"
          ) {
            // UNDO : 骨架屏检查
            wrapperElements++;
          }
        });

        // 如果 80% 的采样点都是空壳，判定白屏
        if (wrapperElements >= points.length * 0.8) {
          const log = {
            type: "whitescreen_error",
            reason: "检测到白屏！"
          };
          this.report(log);
        }
      }, 3000);
    });
  }

  // 数据上报：别用 AJAX
  // 如果页面正在关闭或卸载（用户看到白屏气得关网页），AJAX 请求很可能会被浏览器切断（Canceled），导致上报失败
  // 标准方案：navigator.sendBeacon
  // 不占用主线程：它把数据放入浏览器的传输队列，浏览器会在空闲时发送。
  // 页面关闭也能发：即使用户强制关闭 Tab，浏览器也会保证把这条请求发出去。
  report(data: Record<string, any>) {
    const payload = JSON.stringify({
      ...data,
      appId: this.appId,
      time: readableTimestamp()
    });
    // errorLog(payload);
    console.error(payload);
    if (this.reportUrl) {
      navigator.sendBeacon(this.reportUrl, payload);
    }
  }
}
